import { CHANNEL_TYPE } from "@/enums/ai";
import { ConversationMessage } from "@/services/wingman";
import {
  ConversationClosedReason,
  ConversationDetails,
  ConversationStatus,
} from "@/types/conversations";
import { apiFetch, apiFetchWithStatus } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";
import { normalizeFeedback } from "@/utils/feedback";

const CHANNEL_TO_CONVERSATION_TYPE: Record<string, string> = {
  [CHANNEL_TYPE.CHAT]: "chat",
  [CHANNEL_TYPE.GPT]: "gpt",
  [CHANNEL_TYPE.SMS]: "sms",
  [CHANNEL_TYPE.WHATSAPP]: "whatsapp",
};

export interface GailGptConversationResponse {
  data: GailGptConversation[];
  page: number;
  pages: number;
  size: number;
  total?: number;
}

export interface GailGptConversation {
  type: string;
  assistantId: string;
  createdAt: string;
  id: string;
  isArchived: boolean;
  lastModifiedAt: string;
  name: string;
  userId: string;
  phoneNumber?: string;
  // Lifecycle fields — all optional while the backend rolls them out for
  // messaging conversations. The UI degrades to em-dash / hidden when
  // these are missing.
  status?: ConversationStatus;
  closedAt?: string;
  closedReason?: ConversationClosedReason;
  outcome?: string;
}

export interface GetConversationsOptions {
  type?: CHANNEL_TYPE;
  userId?: string;
  size?: number;
  page?: number;
  includeArchived?: boolean;
  search?: string;
  sortBy?: "Id" | "CreatedAt" | "LastModifiedAt" | null;
  after?: string; // Cursor for forward pagination (ISO 8601 timestamp)
  before?: string; // Cursor for backward pagination (ISO 8601 timestamp)
}

export interface ConversationMessageResponse {
  data: ConversationMessage[];
  page: number;
  pages: number;
  size: number;
  total: number;
}

export interface GetConversationMessagesOptions {
  limit?: number;
  beforeId?: string;
  silent?: boolean;
}

/**
 * Append pagination params depending on whether the caller requested
 * cursor-based (sortBy + after/before) or offset-based (size/page)
 * traversal. Extracted from `getConversations` to keep the latter's
 * cognitive complexity under Sonar's threshold.
 */
function appendPaginationParams(
  queryParams: URLSearchParams,
  options: GetConversationsOptions
): void {
  const { sortBy, size = 50, page, after, before } = options;

  if (sortBy) {
    queryParams.append("order_by", sortBy.toLowerCase());
    queryParams.append("limit", size.toString());
    if (after) queryParams.append("after", after);
    if (before) queryParams.append("before", before);
    return;
  }

  queryParams.append("size", size.toString());
  if (page) queryParams.append("page", page.toString());
}

/**
 * Append user-id + search params. Backend only narrows on these two
 * dimensions today — date / outcome / assistant filters live behind a
 * separate, backend-pending PR.
 */
function appendFilterParams(
  queryParams: URLSearchParams,
  options: GetConversationsOptions
): void {
  const { userId, search } = options;
  if (userId) queryParams.append("userId", userId);
  if (search && search.trim().length > 0) {
    queryParams.append("search", search.trim());
  }
}

function normalizeMessageFeedback(
  msg: ConversationMessage
): ConversationMessage {
  return { ...msg, feedback: normalizeFeedback(msg.feedback) };
}

/**
 * Browser-side Galactus conversation client. Requests authenticate with the
 * session cookie, so a stale access token recovers through the client
 * refresh-and-retry recipe in `apiFetchRaw`.
 */
class GailGptConversations {
  static async getConversations(options: GetConversationsOptions = {}) {
    const { type = "gpt", includeArchived = false } = options;

    const apiType = CHANNEL_TO_CONVERSATION_TYPE[type] ?? type;
    const queryParams = new URLSearchParams({
      type: apiType,
      includeArchived: includeArchived.toString(),
    });

    appendPaginationParams(queryParams, options);
    appendFilterParams(queryParams, options);

    const finalUrl = `${GAIL_API}/.internal/conversations?${queryParams.toString()}`;
    return await apiFetchWithStatus<GailGptConversationResponse>(finalUrl, "GET");
  }

  static async getConversationDetails(conversationId: string) {
    return await apiFetch<ConversationDetails>(
      `${GAIL_API}/.internal/conversations/${conversationId}`,
      "GET"
    );
  }

  static async updateConversationName(conversationId: string, name: string) {
    return await apiFetchWithStatus<GailGptConversation>(
      `${GAIL_API}/.internal/conversations/${conversationId}`,
      "PUT",
      { body: { name } }
    );
  }

  /**
   * Close an open conversation. Idempotent on the backend — calling on an
   * already-closed conversation is a no-op. Triggers the
   * `conversation_finished` webhook and (once shipped) the data-extraction
   * pipeline.
   */
  static async closeConversation(conversationId: string) {
    return await apiFetchWithStatus<ConversationDetails>(
      `${GAIL_API}/.internal/conversations/${conversationId}/close`,
      "POST"
    );
  }

  /** One page of messages, newest first, with feedback normalized. */
  static async getConversationMessages(
    conversationId: string,
    options: GetConversationMessagesOptions = {}
  ): Promise<ConversationMessage[]> {
    const response = await fetchMessagePage(conversationId, options);
    return (response?.data || []).map(normalizeMessageFeedback);
  }

  /**
   * Fetch all messages for a conversation by paginating through Galactus.
   * Returns messages sorted chronologically (ascending by UUID v7 ID).
   *
   * Pass `singlePage: true` to fetch only the first page (useful for
   * lightweight existence checks inside retry loops).
   */
  static async getAllConversationMessages(
    conversationId: string,
    options?: { singlePage?: boolean; silent?: boolean }
  ): Promise<ConversationMessage[]> {
    const pageSize = 100;
    const allMessages: ConversationMessage[] = [];
    let beforeId: string | undefined;
    let totalPages = 1;

    for (let page = 1; page <= totalPages; page++) {
      const response = await fetchMessagePage(conversationId, {
        limit: pageSize,
        beforeId,
        silent: options?.silent,
      });
      const messages = response?.data || [];
      if (messages.length === 0) break;

      allMessages.push(...messages);
      totalPages = response?.pages ?? page;

      if (options?.singlePage) break;
      beforeId = messages[messages.length - 1].id;
    }

    return allMessages
      .map(normalizeMessageFeedback)
      .sort((a, b) => a.id.localeCompare(b.id));
  }
}

async function fetchMessagePage(
  conversationId: string,
  options: GetConversationMessagesOptions
) {
  const { limit = 50, beforeId, silent } = options;

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
  });

  if (beforeId) {
    queryParams.append("before", beforeId);
  }

  return await apiFetch<ConversationMessageResponse>(
    `${GAIL_API}/.internal/conversations/${conversationId}/messages?${queryParams.toString()}`,
    "GET",
    { silent }
  );
}

export default GailGptConversations;
